import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactToPrint from 'react-to-print';
import OrderAddressPrintLabel from '../address-print/OrderAddressPrintLabel';
import MedicineInvoicePrintFrame from './MedicineInvoicePrintFrame';
import ServiceDeliveryInvoice from './ServiceDeliveryInvoice';
import {
    getMedicinePrintUrlsForOrder,
    getServicePaymentItems,
} from './printPaymentItemInvoices';
import './PaymentInvoicesPrint.scss';

const PRINT_READY_DELAY_MS = 500;

/**
 * Print order: service invoices + address on one page, then Evital/medicine invoice(s).
 */
const PaymentInvoicesPrint = ({ paymentItems, sourceData, addressPrint, onDone }) => {
    const printRef = useRef(null);
    const [iframesLoaded, setIframesLoaded] = useState(0);

    const medicinePrintUrls = useMemo(
        () => getMedicinePrintUrlsForOrder(paymentItems, sourceData),
        [paymentItems, sourceData]
    );

    const serviceItems = useMemo(
        () => [...getServicePaymentItems(paymentItems)].sort((a, b) => (a.id || 0) - (b.id || 0)),
        [paymentItems]
    );

    const showAddressSection = Boolean(addressPrint?.address);
    const hasServiceOrAddress = serviceItems.length > 0 || showAddressSection;

    const medicineIframeCount = medicinePrintUrls.length;
    const allIframesReady = medicineIframeCount === 0 || iframesLoaded >= medicineIframeCount;

    const handleIframeLoad = useCallback(() => {
        setIframesLoaded((count) => count + 1);
    }, []);

    useEffect(() => {
        const hasContent =
            medicinePrintUrls.length > 0 || serviceItems.length > 0 || showAddressSection;
        if (!hasContent || !allIframesReady) {
            return undefined;
        }

        const timer = setTimeout(() => {
            document.getElementById('payment-invoices-print-trigger')?.click();
        }, PRINT_READY_DELAY_MS);

        return () => clearTimeout(timer);
    }, [medicinePrintUrls.length, serviceItems.length, showAddressSection, allIframesReady]);

    const handleAfterPrint = () => {
        onDone?.();
    };

    const hasContent =
        medicinePrintUrls.length > 0 || serviceItems.length > 0 || showAddressSection;
    if (!hasContent) {
        return null;
    }

    return (
        <div className="payment-invoices-print-host">
            <ReactToPrint
                trigger={() => (
                    <button
                        id="payment-invoices-print-trigger"
                        type="button"
                        style={{ display: 'none' }}
                        aria-hidden
                    >
                        Print
                    </button>
                )}
                content={() => printRef.current}
                onAfterPrint={handleAfterPrint}
                pageStyle={`
                    @page {
                        size: auto;
                        margin: 0.3in 0.4in;
                    }
                    @media print {
                        html, body {
                            width: 100%;
                            margin: 0;
                            padding: 0;
                        }
                    }
                `}
            />
            <div ref={printRef} className="payment-invoices-print-root">
                {hasServiceOrAddress && (
                    <div className="payment-invoices-print-group payment-invoices-print-group--service-address">
                        {serviceItems.map((paymentItem, index) => (
                            <div
                                key={paymentItem.id || `service-${index}`}
                                className="payment-invoices-print-service-item"
                            >
                                <ServiceDeliveryInvoice
                                    paymentItem={paymentItem}
                                    sourceData={sourceData}
                                    compact
                                />
                            </div>
                        ))}

                        {showAddressSection && (
                            <div className="payment-invoices-address-section">
                                <OrderAddressPrintLabel
                                    address={addressPrint.address}
                                    orderId={addressPrint.orderId}
                                    billNo={addressPrint.billNo}
                                    mapUserPatient={addressPrint.mapUserPatient}
                                />
                            </div>
                        )}
                    </div>
                )}

                {medicinePrintUrls.map((printUrl, index) => (
                    <div
                        key={`medicine-${index}`}
                        className="payment-invoices-print-section payment-invoices-print-section--medicine"
                    >
                        <MedicineInvoicePrintFrame
                            printUrl={printUrl}
                            onLoad={handleIframeLoad}
                            title={`Medicine invoice ${index + 1}`}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

PaymentInvoicesPrint.propTypes = {
    paymentItems: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    sourceData: PropTypes.object,
    addressPrint: PropTypes.shape({
        address: PropTypes.object,
        orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        billNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        mapUserPatient: PropTypes.object,
    }),
    onDone: PropTypes.func,
};

export default PaymentInvoicesPrint;
